/**
 * Eine Zeile der Terminliste — geteilt zwischen dem Reiter *Termine* eines
 * Objekts (`object-panel-termine`) und, künftig, dem Kalender (Spec 07). Kein
 * eigener Bildschirm für einen Termin (er ist keine Entity, AK-427) — ein
 * Klick öffnet `duedate-sheet` zum Ändern, dieselbe Nicht-Navigation wie
 * `TaskRow`.
 *
 * `duedate-row-path-<id>` zeigt `subject.path_label`, nicht `subject.label`:
 * Anders als `TaskRow`/`subjectLabel()` (`features/tasks/labels.ts`, dort ein
 * Masken-Fallback für ein nullable Feld) ist `DueDateSubject.label` nie
 * maskiert (AK-569) — der Bezeichner „…-path-…" und `MoveSheet.tsx`, das
 * `path_label` bereits als Objektpfad zeigt, legen die Lesart hier fest.
 * Gerade am Termin ist der Pfad die informativere Wahl: Ein Termin, der über
 * `?scope=` an einem Nachfahren sichtbar wird, wäre mit dem bloßen
 * Objektnamen sonst nicht von einem Termin am aktuellen Objekt zu
 * unterscheiden (Spec, „Der Reiter Termine am Objekt zeigt das Objekt und
 * seine Nachfahren").
 *
 * `state`/`recurrence_label` kommen vom Server, ungerechnet (I9) — kein
 * Client vergleicht Daten oder bildet „alle N Monate" selbst.
 */

import { assigneeLabel } from '@/features/schedule/labels'
import type { DueDateRow as DueDateRowType } from '@/features/schedule/api'
import { formatDate } from '@/lib/date'
import { PopoverMenu } from '@/components/PopoverMenu'

interface DueDateRowProps {
  readonly dueDate: DueDateRowType
  readonly onOpen: () => void
  readonly onComplete: () => void
  readonly onDisable: () => void
  readonly onEnable: () => void
}

export function DueDateRow({ dueDate, onOpen, onComplete, onDisable, onEnable }: DueDateRowProps) {
  const assignee = assigneeLabel(dueDate.task_assignee)
  const isDisabled = dueDate.state.value === 'disabled'
  const canComplete = dueDate.state.value !== 'disabled' && dueDate.state.value !== 'completed'

  return (
    <li
      data-testid={`duedate-row-${dueDate.id}`}
      className="border-border flex min-h-(--row-h) flex-col gap-1 border-b px-(--page-x) py-2 last:border-b-0 md:h-(--row-h) md:flex-row md:items-center md:gap-3 md:py-0"
    >
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 flex-col items-start gap-1 text-left md:flex-row md:items-center md:gap-3"
      >
        <span className="flex min-w-0 items-center gap-2 md:flex-1">
          <span className="text-body text-text truncate font-medium">{dueDate.title}</span>
          <span className="text-label text-muted shrink-0">{dueDate.recurrence_label}</span>
        </span>

        <span className="text-label text-muted flex flex-wrap items-center gap-x-1 gap-y-0.5 md:contents">
          <span data-testid={`duedate-row-path-${dueDate.id}`} className="truncate md:w-48 md:shrink-0">
            {dueDate.subject.path_label}
          </span>
          <span aria-hidden="true" className="md:hidden">
            ·
          </span>
          <span className="shrink-0 md:w-24">{formatDate(dueDate.due_on)}</span>
          <span aria-hidden="true" className="md:hidden">
            ·
          </span>
          <span className="shrink-0 truncate md:w-32">{assignee ?? '—'}</span>
          <span aria-hidden="true" className="md:hidden">
            ·
          </span>
          {/* W22: Farbe nur für „Überfällig" — der einzige Zustand mit
           * eigenem Token im `design-system`-Skill; „Steht an" und
           * „Deaktiviert" bleiben neutral. `is_overdue` kommt eigens vom
           * Server (I9), nicht aus `state.value` abgeleitet — beide Felder
           * stehen unabhängig auf `DueDateRow`. Der Skill verlangt in Listen
           * den 3-px-Rand statt einer flächigen Einfärbung, dieselbe Klasse
           * wie `statusBorderClass` in `features/billing/status.ts`. */}
          <span
            data-testid={`duedate-row-state-${dueDate.id}`}
            className={`border-border shrink-0 rounded-card border px-2 py-0.5 ${
              dueDate.is_overdue ? 'border-l-status border-l-status-overdue' : ''
            }`}
          >
            {dueDate.state.label}
          </span>
        </span>
      </button>

      <div className="flex shrink-0 items-center gap-2 self-end md:self-auto">
        {canComplete && (
          <button
            type="button"
            data-testid={`duedate-complete-${dueDate.id}`}
            onClick={(event) => {
              event.stopPropagation()
              onComplete()
            }}
            className="border-border text-text hover:bg-bg rounded-card text-label shrink-0 border px-2 py-1 transition-colors duration-(--duration-fast) ease-(--ease-out)"
          >
            Erledigt
          </button>
        )}
        <DueDateRowMenu id={dueDate.id} isDisabled={isDisabled} onDisable={onDisable} onEnable={onEnable} />
      </div>
    </li>
  )
}

/** Das ⋯-Menü der Zeile — geteilte Form mit `AccountMenu`/`LedgerRowMenu`
 * über `components/PopoverMenu.tsx` (`TenantLedger.tsx`). */
function DueDateRowMenu({
  id,
  isDisabled,
  onDisable,
  onEnable,
}: {
  readonly id: string
  readonly isDisabled: boolean
  readonly onDisable: () => void
  readonly onEnable: () => void
}) {
  return (
    <PopoverMenu
      triggerTestId={`duedate-actions-${id}`}
      triggerLabel="Weitere Aktionen"
      triggerClassName="text-muted hover:text-text flex min-h-(--tap-min) min-w-(--tap-min) shrink-0 items-center justify-center"
      menuClassName="border-border bg-surface rounded-card absolute right-0 z-50 mt-1 w-44 border"
      trigger={<span aria-hidden="true">⋯</span>}
    >
      {(close) =>
        isDisabled ? (
          <button
            type="button"
            role="menuitem"
            data-testid={`duedate-enable-${id}`}
            onClick={() => {
              close()
              onEnable()
            }}
            className="text-body text-text hover:bg-bg flex min-h-(--tap-min) w-full items-center px-3 text-left transition-colors duration-(--duration-fast) ease-(--ease-out)"
          >
            Wieder aktivieren
          </button>
        ) : (
          <button
            type="button"
            role="menuitem"
            data-testid={`duedate-disable-${id}`}
            onClick={() => {
              close()
              onDisable()
            }}
            className="text-body text-text hover:bg-bg flex min-h-(--tap-min) w-full items-center px-3 text-left transition-colors duration-(--duration-fast) ease-(--ease-out)"
          >
            Deaktivieren
          </button>
        )
      }
    </PopoverMenu>
  )
}
